import { JobSpeciality } from './generated/graphql';

/**
 * Specialties that belong to a law enforcement context.
 * These use officer/police-specific language, report types, and system prompts.
 */
export const LAW_ENFORCEMENT_SPECIALITIES: readonly JobSpeciality[] = [
  JobSpeciality.PublicSafety,
  JobSpeciality.PatrolAiSuggestions,
  JobSpeciality.InterviewRoom,
  JobSpeciality.RedactionAsAService,
  JobSpeciality.SelfServiceBwcRedaction,
  JobSpeciality.WarrantFirstDraft,
  JobSpeciality.InternalAffairsInterviewFirstDraft,
] as const;

/**
 * Specialties that use the AI report generation pipeline but are NOT law enforcement.
 * These use neutral language (no officer/police terminology).
 */
export const NON_LE_REPORT_SPECIALITIES: readonly JobSpeciality[] = [
  JobSpeciality.FirstDraft,
  JobSpeciality.FraudInvestigation,
  JobSpeciality.PsychEval,
  JobSpeciality.PepPreEmploymentInterview,
  JobSpeciality.ComplianceBehavioralAnalyst,
  JobSpeciality.InsuranceLossAdjuster,
  JobSpeciality.QuoteToEstimate,
  JobSpeciality.SiteWalk,
  JobSpeciality.QualityOfLifeCompliance,
] as const;

/**
 * Specialties whose report depends on knowing who said what, so speaker detection runs even
 * when the owning team has not opted into speaker suggestion.
 */
export const SPEAKER_DETECTION_SPECIALITIES: readonly JobSpeciality[] = [
  JobSpeciality.InterviewRoom,
  JobSpeciality.PsychEval,
  JobSpeciality.PepPreEmploymentInterview,
  JobSpeciality.InternalAffairsInterviewFirstDraft,
  JobSpeciality.InsuranceLossAdjuster,
  JobSpeciality.QuoteToEstimate,
  JobSpeciality.SiteWalk,
] as const;

/**
 * All specialties that trigger the report generation pipeline (public safety plugin).
 */
export const REPORT_GENERATION_SPECIALITIES: readonly JobSpeciality[] = [
  ...LAW_ENFORCEMENT_SPECIALITIES,
  ...NON_LE_REPORT_SPECIALITIES,
  JobSpeciality.Custom,
] as const;

export const DUAL_PLUS_PUBLIC_SAFETY_SPECIALITIES: readonly JobSpeciality[] = [
  JobSpeciality.PublicSafety,
  JobSpeciality.PatrolAiSuggestions,
] as const;

export function getJobSpecialityLabel(speciality: JobSpeciality): string {
  switch (speciality) {
    case JobSpeciality.PublicSafety:
      return 'Patrol 1st Draft';
    case JobSpeciality.PatrolAiSuggestions:
      return 'Patrol AI Suggestions';
    case JobSpeciality.FirstDraft:
      return 'First draft';
    case JobSpeciality.InterviewRoom:
      return 'Interview Room';
    case JobSpeciality.FraudInvestigation:
      return 'Fraud Investigation';
    case JobSpeciality.RedactionAsAService:
      return 'Redaction as a Service';
    case JobSpeciality.SelfServiceBwcRedaction:
      return 'Self-serve BWC redaction';
    case JobSpeciality.PsychEval:
      return 'Psychological Evaluation';
    case JobSpeciality.PepPreEmploymentInterview:
      return 'PEP - Pre-employment interview';
    case JobSpeciality.ComplianceBehavioralAnalyst:
      return 'Compliance & Behavioral Analyst';
    case JobSpeciality.DocumentTranslation:
      return 'Document Translation';
    case JobSpeciality.WarrantFirstDraft:
      return 'Warrant Aff. 1st draft';
    case JobSpeciality.InternalAffairsInterviewFirstDraft:
      return 'Internal Affairs Interview 1st draft';
    case JobSpeciality.InsuranceLossAdjuster:
      return 'Insurance Loss Adjuster';
    case JobSpeciality.QuoteToEstimate:
      return 'Quote to Estimate';
    case JobSpeciality.SiteWalk:
      return 'Site Walk';
    case JobSpeciality.QualityOfLifeCompliance:
      return 'Quality of Life Compliance';
    case JobSpeciality.Custom:
      return 'Custom';
    case JobSpeciality.Medical:
      return 'Medical';
    case JobSpeciality.Standard:
      return 'Standard';
    default:
      return speciality;
  }
}

export function isLawEnforcementSpeciality(speciality: string | null | undefined): boolean {
  return (LAW_ENFORCEMENT_SPECIALITIES as readonly string[]).includes(speciality as string);
}

export function isReportGenerationSpeciality(speciality: string | null | undefined): boolean {
  return (REPORT_GENERATION_SPECIALITIES as readonly string[]).includes(speciality as string);
}

export function isDualPlusPublicSafetySpeciality(speciality: string | null | undefined): boolean {
  return (DUAL_PLUS_PUBLIC_SAFETY_SPECIALITIES as readonly string[]).includes(speciality as string);
}

export function isCustomSpeciality(speciality: string | null | undefined): boolean {
  return speciality === JobSpeciality.Custom;
}

export function requiresSpeakerDetection(speciality: string | null | undefined): boolean {
  return (SPEAKER_DETECTION_SPECIALITIES as readonly string[]).includes(speciality as string);
}

/**
 * Sheets for specialities whose report reads badly on the A4 portrait default. Quality of Life
 * Compliance is a six-column scorecard. A trained report format's own page size still wins.
 */
const DEFAULT_REPORT_PAGE_SIZES: Partial<Record<string, { width: number; height: number }>> = {
  [JobSpeciality.QualityOfLifeCompliance]: { width: 11.69, height: 8.27 },
};

export function getDefaultReportPageSize(
  speciality: string | null | undefined,
): { width: number; height: number } | undefined {
  return speciality ? DEFAULT_REPORT_PAGE_SIZES[speciality] : undefined;
}
